import {
  Box,
  Button,
  Drawer,
  Flex,
  Loader,
  Modal,
  Select,
  Table,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { getAttendanceReport } from "../../services/staff";
import { decrypt } from "../../utils";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { addBrandedHeader, addBrandedFooter } from "../BrandedReport";
import { S, BRAND, PdfIcon } from "../BrandedReport/reportStyles";

const formatSeconds = (seconds) => {
  if (!seconds || isNaN(seconds)) return "-";
  const totalMinutes = Math.floor(Number(seconds) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

const formatTime = (value) => {
  if (!value) return "-";
  return moment(value).format("hh:mm A");
};

const formatDate = (record) => {
  if (record?.date?.date) return moment(record?.date?.date).format("MM/DD/YYYY");
  if (record?.startTime) return moment(record?.startTime).format("MM/DD/YYYY");
  return "-";
};

const getStaffName = (staff) => {
  if (!staff) return "-";
  if (staff?.firstName || staff?.lastName)
    return `${staff?.firstName || ""} ${staff?.lastName || ""}`.trim();
  return staff?.name || "-";
};

const exportToCSV = (records, dateFrom, dateTo, staffLabel) => {
  const dateRangeLabel =
    dateFrom && dateTo
      ? `${dateFrom} - ${dateTo}`
      : dateFrom
      ? `From ${dateFrom}`
      : dateTo
      ? `To ${dateTo}`
      : "All Dates";

  const headers = [
    "Staff Name",
    "Date",
    "Clock In",
    "Clock Out",
    "Total Hours Worked",
    "Facility",
    "Client",
  ];

  const rows = records?.map((r) => [
    getStaffName(r?.staff),
    formatDate(r),
    formatTime(r?.startTime),
    formatTime(r?.endTime),
    formatSeconds(r?.total_work_time),
    r?.facility?.name || "-",
    r?.client?.name ? decrypt(r?.client?.name) : "-" || "-",
  ]);

  const toCell = (cell) => `"${String(cell)?.replace(/"/g, '""')}"`;

  const csvContent = [
    `"Staff: ${staffLabel}"`,
    `"Date Range: ${dateRangeLabel}"`,
    "",
    headers.map(toCell).join(","),
    ...rows.map((row) => row.map(toCell).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `time_attendance_report_${moment().format("YYYY-MM-DD")}.csv`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const exportToPDF = (records, dateFrom, dateTo, staffLabel, facilityName) => {
  const doc = new jsPDF("l", "pt", "a4");

  const contentStartY = addBrandedHeader(doc, {
    facilityName: facilityName || "Facility",
    reportTitle: "Time & Attendance Report",
    reportSubtitle: `${dateFrom || ""} ${dateFrom && dateTo ? "-" : ""} ${dateTo || "All Dates"}`.trim(),
    generatedDate: moment().format("MM/DD/YYYY"),
  });

  // Staff filter label
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor("#777777");
  doc.text(`Staff: ${staffLabel}`, 30, contentStartY);

  const tableData = records?.map((r) => [
    getStaffName(r?.staff),
    formatDate(r),
    formatTime(r?.startTime),
    formatTime(r?.endTime),
    formatSeconds(r?.total_work_time),
    r?.facility?.name || "-",
    r?.client?.name ? decrypt(r?.client?.name) : "-",
  ]);

  autoTable(doc, {
    head: [["Staff Name", "Date", "Clock In", "Clock Out", "Total Hours", "Facility", "Client"]],
    body: tableData,
    startY: contentStartY + 10,
    styles: { fontSize: 8, cellPadding: 4, font: "helvetica" },
    headStyles: {
      fillColor: [29, 41, 57],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7,
      halign: "left",
    },
    alternateRowStyles: { fillColor: [248, 248, 246] },
    margin: { left: 30, right: 30 },
  });

  addBrandedFooter(doc);
  doc.save(`time_attendance_report_${moment().format("YYYY-MM-DD")}.pdf`);
};

const StaffAttendanceReportDrawer = ({ opened, onClose }) => {
  const { allStaffWithSignature } = useSelector((state) => state.staff);
  const { profile } = useSelector((state) => state?.profile);

  const [staffId, setStaffId] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  const staffOptions = (allStaffWithSignature || []).map((s) => ({
    value: s?._id,
    label:
      s?.firstName || s?.lastName
        ? `${s?.firstName || ""} ${s?.lastName || ""}`.trim()
        : s?.name || s?.email || s?._id,
  }));

  const selectedStaffLabel =
    staffOptions?.find((s) => s?.value === staffId)?.label || "All Staff";

  const selectedDateRangeLabel =
    dateRange[0] && dateRange[1]
      ? `${moment(dateRange[0]).format("MM/DD/YYYY")} – ${moment(dateRange[1]).format("MM/DD/YYYY")}`
      : dateRange[0]
      ? `From ${moment(dateRange[0]).format("MM/DD/YYYY")}`
      : dateRange[1]
      ? `To ${moment(dateRange[1]).format("MM/DD/YYYY")}`
      : "All Dates";

  const csvDateFrom = dateRange[0] ? moment(dateRange[0]).format("MM/DD/YYYY") : "";
  const csvDateTo = dateRange[1] ? moment(dateRange[1]).format("MM/DD/YYYY") : "";

  const facilityName = profile?.facility?.name || "Facility";

  const handleGenerate = async () => {
    setLoading(true);
    const filters = {
      staffId: staffId || undefined,
      dateFrom: dateRange[0]
        ? moment(dateRange[0]).format("YYYY-MM-DD")
        : undefined,
      dateTo: dateRange[1]
        ? moment(dateRange[1]).format("YYYY-MM-DD")
        : undefined,
    };
    const data = await getAttendanceReport(filters);
    setRecords(data || []);
    setLoading(false);
    setReportModalOpen(true);
  };

  const handleClose = () => {
    setRecords([]);
    setStaffId("");
    setDateRange([null, null]);
    onClose();
  };

  // Compute summary stats
  const totalHoursWorked = records?.reduce((acc, r) => acc + (Number(r?.total_work_time) || 0), 0);

  return (
    <>
      <Drawer
        opened={opened}
        onClose={handleClose}
        title={
          <Text fw={600} fz={16} ff="Inter">
            Time &amp; Attendance Report
          </Text>
        }
        position="right"
        size="clamp(320px, 100vw, 500px)"
        padding="md"
        radius="md"
        zIndex={1000}
        overlayProps={{
          color: "black",
          opacity: 0.5,
          blur: 5,
        }}
        overlayOpacity={0.5}
      >
        <Flex direction="column" gap={14}>
          <Select
            label="Staff Member"
            placeholder="Select staff"
            data={staffOptions}
            value={staffId}
            onChange={setStaffId}
            clearable
            searchable
            radius="md"
          />

          <DatePickerInput
            type="range"
            label="Date Range"
            placeholder="Select date range"
            value={dateRange}
            onChange={setDateRange}
            clearable
            radius="md"
          />

          <Button
            onClick={handleGenerate}
            loading={loading}
            color="yellow"
            c="#000"
            radius="md"
            mt={4}
          >
            Generate Report
          </Button>
        </Flex>
      </Drawer>

      <Modal
        opened={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        title={
          <Text fw={600} fz={16} ff="Inter">
            Time &amp; Attendance Report
          </Text>
        }
        size="90%"
        radius="md"
        centered
        fullScreen={typeof window !== "undefined" && window.innerWidth <= 600}
        overlayZIndex={1000}
        zIndex={1100}
        overlayProps={{ color: "black", opacity: 0.5, blur: 5 }}
        overlayOpacity={0.5}
      >
        {loading ? (
          <Flex justify="center" my={40}>
            <Loader color="yellow" />
          </Flex>
        ) : (
          <>
            {/* ── Branded Header ── */}
            <Box style={{
              ...S.pageHeader,
              borderBottom: `2px solid ${BRAND.bodyText}`,
              paddingBottom: 10,
              marginBottom: 14,
            }}>
              <div style={S.branding}>
                <div style={S.logoCircle}>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                    {facilityName?.[0] || "O"}
                  </span>
                </div>
                <div>
                  <div style={S.facilityName}>{facilityName}</div>
                  <div style={S.facilitySub}>Time &amp; Attendance Report</div>
                </div>
              </div>
              <div style={S.reportRight}>
                <div style={S.reportTitle}>Time &amp; Attendance</div>
                <div style={S.reportSub}>{selectedDateRangeLabel}</div>
              </div>
            </Box>

            {/* ── Meta Grid ── */}
            <div style={S.metaBlock}>
              <div style={S.metaCell}>
                <div style={S.metaLabel}>Staff</div>
                <div style={S.metaValue}>{selectedStaffLabel}</div>
              </div>
              <div style={S.metaCell}>
                <div style={S.metaLabel}>Date Range</div>
                <div style={S.metaValue}>{selectedDateRangeLabel}</div>
              </div>
              <div style={S.metaCell}>
                <div style={S.metaLabel}>Total Records</div>
                <div style={S.metaValue}>{records?.length || 0}</div>
              </div>
              <div style={S.metaCellLast}>
                <div style={S.metaLabel}>Total Hours</div>
                <div style={S.metaValue}>{formatSeconds(totalHoursWorked)}</div>
              </div>
            </div>

            {/* ── Export Buttons ── */}
            <Flex justify="flex-end" gap={10} mb={14}>
              <Button
                onClick={() => exportToPDF(records, csvDateFrom, csvDateTo, selectedStaffLabel, facilityName)}
                variant="subtle"
                color="dark"
                radius="md"
                size="xs"
                leftIcon={<PdfIcon size={22} />}
                disabled={records.length === 0}
              >
                PDF
              </Button>
              <Button
                onClick={() => exportToCSV(records, csvDateFrom, csvDateTo, selectedStaffLabel)}
                radius="md"
                color="yellow"
                c="#000"
                size="xs"
                disabled={records.length === 0}
              >
                Download CSV
              </Button>
            </Flex>

            {/* ── Section Header ── */}
            <div style={S.sectionHeader}>Attendance Records</div>

            {records.length === 0 ? (
              <Flex justify="center" my={40}>
                <Text c="dimmed" fz={14} ff="Inter">
                  No records found for the selected filters.
                </Text>
              </Flex>
            ) : (
              <Box style={{ overflowX: "auto" }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Staff Name</th>
                      <th style={S.th}>Date</th>
                      <th style={S.th}>Clock In</th>
                      <th style={S.th}>Clock Out</th>
                      <th style={S.th}>Total Hours</th>
                      <th style={S.th}>Facility</th>
                      <th style={{ ...S.th, borderRight: "none" }}>Client</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records?.map((r, i) => {
                      const rowBg = i % 2 === 0 ? BRAND.white : "#f8f8f6";
                      return (
                        <tr key={r?._id || i} style={{ background: rowBg }}>
                          <td style={S.td}>{getStaffName(r?.staff)}</td>
                          <td style={S.td}>{formatDate(r)}</td>
                          <td style={S.td}>{formatTime(r?.startTime)}</td>
                          <td style={S.td}>{formatTime(r?.endTime)}</td>
                          <td style={S.td}>{formatSeconds(r?.total_work_time)}</td>
                          <td style={S.td}>{r?.facility?.name || "-"}</td>
                          <td style={{ ...S.td, borderRight: "none" }}>{r?.client?.name ? decrypt(r?.client?.name) : "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            )}

            {/* ── Branded Footer ── */}
            <div style={S.footer}>
              <span>Confidential — Report generated by Ogiso</span>
              <span>{moment().format("MM/DD/YYYY")}</span>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default StaffAttendanceReportDrawer;
